import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { IconDefinition } from '@fortawesome/pro-duotone-svg-icons';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import _ from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { isValidEmail } from 'src/app/helper/emailingHelpers';
import { EmailContact } from 'src/app/store/samwin/email/email-behaviour/email-contact';

@Component({
   selector: 'samweb-email-multiselect',
   templateUrl: './email-multiselect.component.html',
   styleUrls: ['./email-multiselect.component.scss'],
})
export class EmailMultiselectComponent implements AfterViewInit {
   constructor(private elementRef: ElementRef) {}

   @Input()
   public icon: IconDefinition;

   @Input()
   public header: string;

   @Input()
   public showWarning = false;

   @Input()
   public fadeIconOpacity = false;

   @Input()
   public swapIconOpacity = false;

   @Input()
   public disabled = false;

   @Input()
   public isFocused = false;

   @Input()
   public highlightIcon = false;

   @Input()
   public autocompleteMatches: EmailContact[];

   @Input()
   public selectedEntries: EmailContact[] = [];

   @Output()
   public valueChanged = new EventEmitter();

   @Output()
   public valueChangedDebounced = new EventEmitter();

   @Output()
   public entrySelected = new EventEmitter();

   @Output()
   public unFocus = new EventEmitter();

   public searchBarValue$ = new BehaviorSubject<string>('');

   @ViewChild('multiselect')
   public multiselect: MultiSelectComponent;

   ngAfterViewInit(): void {
      const multiselectElement = this.elementRef.nativeElement.querySelector('kendo-multiselect');
      const divElement = multiselectElement?.querySelector('div');
      if (!_.isNil(divElement)) {
         divElement.style.border = 'none';
      }
   }

   public valueNormalizer = (text$: Observable<string>) =>
      text$.pipe(
         map((email: string) => {
            return new EmailContact(email);
         }),
         map((contact) => (!contact.isDuplicate(this.selectedEntries) ? contact : null))
      );

   textChanged(value: string) {
      if (!this.disabled) {
         this.searchBarValue$.next(value);
         this.valueChanged.emit(value);
      }
   }

   textChangedDebounced(value: string) {
      if (!this.disabled) {
         this.valueChangedDebounced.emit(value);
      }
   }

   clearText() {
      if (!this.disabled) {
         this.valueChanged.emit('');
         this.valueChangedDebounced.emit('');
      }
   }

   onMultiselectValueChange(selectedItems: EmailContact[]) {
      this.entrySelected.emit(selectedItems);
      this.cleanSearchbar();
   }

   changeMultiSelectSearchBar(value: string) {
      this.multiselect.searchbar.valueChange.emit(value);
   }

   onFocusOut(event: FocusEvent) {
      this.unFocus.emit(event);
      this.selectIfValid();
   }

   cleanSearchbar() {
      this.changeMultiSelectSearchBar('');
      this.textChanged('');
      this.textChangedDebounced('');
   }

   selectIfValid() {
      const currentSearchValue = this.searchBarValue$.value;
      const distinctEntries: Set<EmailContact> = new Set(this.selectedEntries ?? []);
      const emailContact = new EmailContact(currentSearchValue);
      if (isValidEmail(currentSearchValue) && !emailContact.isDuplicate(this.selectedEntries)) {
         distinctEntries.add(new EmailContact(currentSearchValue));
      }
      this.onMultiselectValueChange(Array.from(distinctEntries));
   }
}
